'use client';
/**
 * LES MENTIONS DU CARTOUCHE, SITUATION PAR SITUATION.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Demande de la RQ (06/10/2026) : « je veux pouvoir paramétrer le contenu des
 * cartouches qui sont insérés selon la situation ». Quatre phrases, écrites
 * jusque-là en dur dans le serveur.
 *
 * Trois partis pris d'écran, chacun pour une raison :
 *
 *  · CHAQUE CHAMP MONTRE SON RÉSULTAT, juste dessous, dans la couleur et sur le
 *    document d'exemple où il s'imprimera. Un modèle à jetons ne se relit pas :
 *    « COPIE MAÎTRISÉE n° {numero} — {lieu} » ne dit pas ce que la feuille
 *    portera. C'est le même raisonnement que l'aperçu de la page entière.
 *  · LES JETONS INCONNUS SONT SIGNALÉS ICI, pas découverts à l'impression. Le
 *    serveur les retire du papier — `{lieux}` ne doit pas partir sur un document
 *    officiel — mais un retrait muet ferait croire à une phrase correcte.
 *  · VIDER UN CHAMP NE SUPPRIME PAS LA MENTION : il revient au texte d'origine.
 *    La traçabilité de l'exemplaire (ISO 15189 §8.3) ne s'éteint pas par une
 *    case blanche. L'écran le DIT, au lieu de laisser croire le contraire.
 */
import React, { useRef } from 'react';
import { inputStyle } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import {
  JETONS, MENTIONS_DEFAUT, SITUATIONS, SITUATION_LIBELLE,
  jetonsInconnus, mentionDe, type SituationMention, type ValeursMention,
} from '@/lib/mentionsCartouche';

/** Les hex du cartouche injecté, repris tels quels (OOXML n'a pas de jetons). */
const ROUGE = 'var(--red-600)';
const VERT = 'var(--green-600)';

/** La couleur dans laquelle la phrase s'imprimera réellement. */
const COULEUR: Record<SituationMention, string> = {
  copieMaitrisee: VERT, copieNonMaitrisee: ROUGE, enValidation: ROUGE, nonApplicable: ROUGE,
};

type Mentions = Partial<Record<SituationMention, string>>;

function Situation({ situation, valeur, valeurs, onChange }: {
  situation: SituationMention;
  valeur: string;
  valeurs: ValeursMention;
  onChange: (v: string) => void;
}) {
  const champ = useRef<HTMLInputElement>(null);
  const libelle = SITUATION_LIBELLE[situation];
  const defaut = MENTIONS_DEFAUT[situation];
  const modifie = valeur.trim() !== '' && valeur.trim() !== defaut;
  const inconnus = jetonsInconnus(valeur, situation);
  const rendu = mentionDe({ [situation]: valeur }, situation, valeurs);
  const vide = valeur.trim() === '';

  /* Le jeton s'insère AU CURSEUR, pas en fin de ligne : on ajoute presque
     toujours « par qui » au milieu d'une phrase, pas derrière le point. */
  const inserer = (cle: string) => {
    const el = champ.current;
    const texte = valeur || defaut;
    const i = el && el.selectionStart !== null ? el.selectionStart : texte.length;
    const suivant = `${texte.slice(0, i)}{${cle}}${texte.slice(i)}`;
    onChange(suivant);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(i + cle.length + 2, i + cle.length + 2);
    });
  };

  return (
    <div style={{ padding: '13px 0', borderTop: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 3 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{libelle.titre}</div>
        {modifie && (
          <Bouton variante="discret" taille="sm" onClick={() => onChange('')}>
            Rétablir le texte d’origine
          </Bouton>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45, marginBottom: 7 }}>{libelle.quand}</div>

      <input
        ref={champ}
        value={valeur}
        placeholder={defaut}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: 12.5 }}
        aria-label={`Mention — ${libelle.titre}`}
      />

      {/* Les jetons utilisables ICI : `{numero}` et `{lieu}` n'ont de valeur que
          sur un exemplaire du registre, les proposer ailleurs serait un piège. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
        {JETONS.filter((j) => j.situations.includes(situation)).map((j) => (
          <button key={j.cle} type="button" onClick={() => inserer(j.cle)} title={j.libelle}
            style={{
              fontFamily: 'var(--font-mono)', fontSize: 11, padding: '2px 7px', cursor: 'pointer',
              borderRadius: 'var(--radius-pill)', border: '1px solid var(--border-default)',
              background: 'var(--surface-card)', color: 'var(--text-body)',
            }}>
            {`{${j.cle}}`}
          </button>
        ))}
      </div>

      {/* CE QUI S'IMPRIMERA — la seule chose que la RQ regarde vraiment. */}
      <div style={{ marginTop: 9, padding: '8px 11px', background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)' }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 3 }}>
          Sur le document
        </div>
        <div style={{ fontFamily: 'Calibri, var(--font-sans)', fontSize: 12.5, fontWeight: 700, color: COULEUR[situation] }}>{rendu}</div>
      </div>

      {vide && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
          Champ vide : c’est le texte d’origine qui s’imprime. Une mention ne peut pas être supprimée.
        </div>
      )}
      {inconnus.length > 0 && (
        <div style={{ fontSize: 12, color: ROUGE, marginTop: 6, fontWeight: 600 }}>
          {inconnus.length > 1 ? 'Jetons inconnus ici' : 'Jeton inconnu ici'} :{' '}
          <span style={{ fontFamily: 'var(--font-mono)' }}>{inconnus.map((j) => `{${j}}`).join(' ')}</span> — ils seront
          retirés du document, pas imprimés.
        </div>
      )}
    </div>
  );
}

export function MentionsSituation({ mentions, valeurs, onChange }: {
  mentions: Mentions | undefined;
  valeurs: ValeursMention;
  onChange: (m: Mentions) => void;
}) {
  const m = mentions || {};
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        Mentions selon la situation
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: '5px 0 2px' }}>
        Ce que la plateforme écrit sur le document selon l’exemplaire et l’état de la version. Les jetons entre
        accolades sont remplacés au moment du tirage ; un jeton sans valeur emporte le séparateur qui le suit.
      </div>
      {SITUATIONS.map((s) => (
        <Situation key={s} situation={s} valeur={m[s] ?? ''} valeurs={valeurs}
          onChange={(v) => onChange({ ...m, [s]: v })} />
      ))}
    </div>
  );
}
